<script setup>
    import { ref, onMounted, defineProps, defineEmits, watch } from 'vue';
    import { db } from '@/firebase';
    import { getDocs, updateDoc, query, where, collection, arrayUnion, arrayRemove } from 'firebase/firestore';
    import ApplicationWrapper from '@/pages/externalApplications/ApplicationWrapper.vue';

    const props = defineProps({
        dataID: {
            type: String,
            required: true
        },
        data: {
            type: Object,
            required: true,
            default: () => ({})
        }
    });

    const emit = defineEmits(['requestDownload', 'modalOpen']);

    const dataPaused = ref(false);
    const unitRef = ref(null);
    const showModal = ref(false);
    const potassiumData = ref({value: 'Retrieving...'});
    const nitrateData = ref({value: 'Retrieving...'});

    onMounted(async () => {
        // Get the paused data from the database
        const unitsRef = collection(db, "units");
        const q = query(unitsRef, where("dataID", "==", props.dataID));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            console.error("No units found");
            return;
        }

        unitRef.value = querySnapshot.docs[0].ref;
        const doc = querySnapshot.docs[0].data();
        const pausedData = doc.pausedData;

        if (pausedData.includes("sensor_PAGESensor")) {
            dataPaused.value = true;
        }
    });

    const pauseData = async () => {
        
        // Update Firestore with the new array
        await updateDoc(unitRef.value, {
            pausedData: arrayUnion("sensor_PAGESensor")
        });
        
        // Update local state
        dataPaused.value = true;
    }

    const resumeData = async () => {
        // Update Firestore with the new array
        await updateDoc(unitRef.value, {
            pausedData: arrayRemove("sensor_PAGESensor")
        });
        
        // Update local state
        dataPaused.value = false;
    }

    const closeModal = () => {
        showModal.value = false;
    }

    const openModal = () => {
        showModal.value = true;
    }

    watch(props.data, (newData) => {
        if (Object.hasOwn(newData, 'sensor_PAGESensor')) {
            if (Object.hasOwn(newData.sensor_PAGESensor, 'analyte_1_value') && Object.hasOwn(newData.sensor_PAGESensor, 'analyte_2_value')) {
                nitrateData.value['value'] = newData.sensor_PAGESensor?.analyte_1_value[newData.sensor_PAGESensor?.x.length - 1] || 'unknown';
                potassiumData.value['value'] = newData.sensor_PAGESensor?.analyte_2_value[newData.sensor_PAGESensor?.x.length - 1] || 'unknown';
                nitrateData.value['timestamp'] = newData.sensor_PAGESensor?.x[newData.sensor_PAGESensor?.x.length - 1] || 'unknown';
                potassiumData.value['timestamp'] = newData.sensor_PAGESensor?.x[newData.sensor_PAGESensor?.x.length - 1] || 'unknown';
            } else {
                nitrateData.value['value'] = 'Could not retrieve data';
                potassiumData.value['value'] = 'Could not retrieve data';
            }
        } else {
            nitrateData.value['value'] = 'Unknown';
            potassiumData.value['value'] = 'Unknown';
        }
    }, { deep: true });
</script>

<template>
    <!-- Custom Page Technologies Code -->
    <div class="flex flex-col md:flex-row mb-5 gap-5">
        <div class="flex flex-col bg-white rounded-lg p-5 text-center w-full md:min-w-[250px]">
            <h2 class="text-xl md:text-2xl font-bold underline mb-4">NO3-</h2>
            <p class="font-bold">Value: <span class="font-normal"> {{ nitrateData.value }}</span></p>
            <p v-if="nitrateData.timestamp" class="font-bold text-sm">Last Updated: <span class="font-normal"> {{ nitrateData.timestamp }}</span></p>
        </div>
        <div class="flex flex-col bg-white rounded-lg p-5 text-center w-full md:min-w-[250px]">
            <h2 class="text-xl md:text-2xl font-bold underline mb-4">K+</h2>
            <p class="font-bold">Value: <span class="font-normal"> {{ potassiumData.value }}</span></p>
            <p v-if="potassiumData.timestamp" class="font-bold text-sm">Last Updated: <span class="font-normal"> {{ potassiumData.timestamp }}</span></p>
        </div>
        <div class="flex-1 bg-white rounded-lg p-5">
            <div class="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-4">
                <div class="flex flex-col gap-2 w-full">
                    <button class="default-button p-3 md:p-5 w-full text-base md:text-lg"
                        @click="openModal"
                    >Calibrate</button>
                    <button v-if="!dataPaused" class="default-button bg-orange-700 hover:bg-orange-600 p-3 md:p-5 w-full text-base md:text-lg"
                        @click="pauseData"
                    >Pause Measurements</button>
                    <button v-else class="default-button bg-green-700 hover:bg-green-600 p-3 md:p-5 w-full text-base md:text-lg"
                        @click="resumeData"
                    >Resume Measurements</button>
                </div>
                <button class="default-button p-3 md:p-5 w-full text-base md:text-lg"
                    @click="emit('requestDownload')"
                >Export Data</button>
            </div>
        </div>
    </div>

    <!-- Modal for Calibration -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg w-full md:w-[80vw] lg:w-[50vw] h-[60vh] md:h-[50vh] relative">
            <button @click="closeModal" class="absolute top-2 right-2 md:top-4 md:right-4 text-gray-500 hover:text-gray-700 z-10 p-2">
                <svg class="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            </button>
            <ApplicationWrapper
                applicationUrl="/page_blth_app"
                sandboxOptions="allow-scripts allow-same-origin"
                :allowCommunication="true"
                ref="appWrapper"
            />
        </div>
    </div>
</template>